<template>
	<div class="modal-container ModalAddEditFee">
		<div class="modal-header">
			<div>
				Fee Details
				<div class="additional-info">
					Origin: {{ feeSourceEnumsHelper.getDisplay(fee.source) }}
					<span v-if="$global.isAdminView && fee">(Title ID: {{ fee.titleId ?? 'N/A' }}, Original: {{ bucket.originalTitleId ?? 'N/A' }})</span>
				</div>
			</div>

			<EditInfo
				v-if="!isNewFee"
				:editableObject="fee"
			/>
		</div>
		<div
			v-if="bucket"
			class="modal-body"
		>
			<fieldset
				:disabled="modal.isModalDisabled"
				class="no-margin-grid"
				:style="$grid('1')"
			>
				<PanelAddEditFeeInfo
					:fee
					:bucket
					:isDisabled="modal.isModalDisabled"
					:isNewFee
					:storeFees
					:bo
					:isDealLocked="modal.isDealLocked"
				/>

				<Panel title="Tax Lines to Apply">
					<fieldset
						class="no-margin-grid"
						:style="$grid('1')"
						:disabled="modal.isDealLocked"
					>
						<div
							class="no-margin-grid"
							:style="$grid('5-1')"
							v-for="(taxLine, taxLineIndex) in bo.taxGroups"
							:key="taxLine.name"
						>
							<InputCheckbox
								:modelValue="isInThisTaxLine(taxLineIndex)"
								@click="toggleTaxLine(taxLineIndex)"
								class="panel-header-collapse-button description"
								:label="taxLine.name"
							></InputCheckbox>

							<div class="amt">
								<InputNumber
									v-model:saturn="taxLine.rate"
									placeholder="Tax Rate"
									:precision="2"
									:isPercent="true"
									:disabled="true"
								>
								</InputNumber>
							</div>
						</div>
					</fieldset>
				</Panel>
			</fieldset>
		</div>
		<div class="modal-footer modal-footer-split">
			<div>
				<button
					v-if="!isNewFee"
					class="button-unwind"
					@click="deleteFee()"
					:disabled="modal.isDealLocked && Boolean(fee.amount || fee.itemized)"
				>
					Delete Fee
				</button>
			</div>
			<div v-if="isEditable">
				<button
					class="button-error"
					@click="cancel()"
				>
					Cancel
				</button>
				<button
					class="button-save"
					@click="save()"
					:disabled="modal.isModalDisabled || v$.$invalid || (modal.isDealLocked && fee.itemized && fee.isEditableItemized && isNewFee)"
				>
					Save
				</button>
			</div>
			<div v-else>
				<button
					class="button-error"
					@click="cancel()"
				>
					Close
				</button>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	export interface IFeesBucketModalProps extends IBucketModalProps {
		storeFees: StoreFee[];
	}
</script>

<script setup lang="ts">
	import { computed, ref, watch } from 'vue';
	import $modal from '@core/services/modal';
	import auth from '@core/services/auth';
	import EditInfo from '@/components/EditInfo.vue';
	import FeeSourceEnumsHelper from '@core/_enums/FeeSourceEnumsHelper';
	import FIMenuBuyersOrder from '@core/classes/FIMenuBuyersOrder';
	import FIMenuBuyersOrderBucket from '@core/classes/FIMenuBuyersOrderBucket';
	import FIMenuBuyersOrderBucketItem from '@core/classes/FIMenuBuyersOrderBucketItem';
	import FIMenuBuyersOrderFee from '@core/classes/FIMenuBuyersOrderFee';
	import { IBucketModalProps } from '@core/_interfaces/ComponentProps';
	import InputCheckbox from '@core/components/InputCheckbox.vue';
	import InputNumber from '@core/components/InputNumber.vue';
	import Panel from '@core/components/Panel.vue';
	import PanelAddEditFeeInfo from '@/components/panel/PanelAddEditFeeInfo.vue';
	import { required } from '@vuelidate/validators';
	import StoreFee from '@core/classes/Store/StoreFee';
	import { useVuelidate } from '@vuelidate/core';
	import util from '@core/services/util';

	const { modal } = defineProps<{
		modal: IFeesBucketModalProps;
	}>();

	const user = auth.getTokenPayload();

	const feeSourceEnumsHelper = new FeeSourceEnumsHelper();

	// Copy Buyer's Order
	const bo = ref<FIMenuBuyersOrder>(new FIMenuBuyersOrder(modal.bo));
	const fee = ref<FIMenuBuyersOrderFee>(null);
	const feeIndex = ref<number>(null);
	const bucket = ref<FIMenuBuyersOrderBucket>(modal.bucketContent.parentBucket);
	const originalName = ref<string>(null);
	const isNewFee = ref(false);
	const storeFees = ref<StoreFee[]>(null);

	const isEditable = computed(() => fee.value.baseType == 'Fixed');

	//STORE DEFAULT FEES
	//WE ARE NOT ALLOWING CUSTOM FEES DUE TO ACCOUNTING MAPPING LOGIC
	//let customFee = new StoreFee({
	//    name: null,
	//    amount: null,
	//    cost: null,
	//    profitFlag: ENUMS.PROFIT_FLAGS.NONE,
	//    isEditableDesc: true,
	//    isEditableCost: true,
	//    isEditableAmount: true,
	//    preloaded: false,
	//    titleId: null
	//});

	//WE ONLY WANT TO ALLOW TO ADD THE SAME FEE ONCE, NAME MUST BE UNIQUE
	//let allFeeNamesAlreadyUsed = bo.value.fees.map(item => item.name);
	//storeFees.value = modal.storeFees.filter(f => !allFeeNamesAlreadyUsed.includes(f.name)).map(f => new StoreFee(f));
	storeFees.value = modal.storeFees.map(f => new StoreFee(f));
	//storeFees.value.push(customFee)

	//IF NEW FEE, CREATE IT AND CREATE A BUCKET FOR IT
	if (!modal.bucketContent.bucketItem) {
		isNewFee.value = true;

		//CREATE NEW FEE
		fee.value = bo.value.addNewDefaultFee(modal.bucketContent.parentBucket.titleId, true, true, true);
	} else {
		isNewFee.value = false;

		// Find corresponding bucket in Buyer's Order copy
		bucket.value = bo.value.buckets.find(
			b =>
				b.bucketType === modal.bucketContent.parentBucket.bucketType &&
				b.originalTitleId === modal.bucketContent.parentBucket.originalTitleId &&
				b.titleId === modal.bucketContent.parentBucket.titleId &&
				b.taxIndex === modal.bucketContent.parentBucket.taxIndex &&
				b.upfrontTaxIndex === modal.bucketContent.parentBucket.upfrontTaxIndex,
		);

		//GET FEE
		fee.value = bo.value.fees.find((f: FIMenuBuyersOrderFee) => f.name == modal.bucketContent.bucketItem.name);
		originalName.value = fee.value.name;

		if (!bucket.value) {
			bucket.value = bo.value.addNewDefaultBucket(bucket.value.bucketType, 0, 0, null, fee.value.titleId);
		}

		feeIndex.value = bo.value.fees.findIndex((tg: FIMenuBuyersOrderFee) => tg === fee.value);
	}

	watch(
		() => fee.value,
		() => {
			bo.value.isManuallyChanged = true;
		},
		{ deep: true },
	);

	//GIVEN A taxLineIndex RETURNS TRUE IF THE CURRENT ITEM'S IS TAXED ON THAT TAX LINE
	function isInThisTaxLine(taxLineIndex: number) {
		const taxIndex = bo.value.isLease() ? bucket.value.upfrontTaxIndex : bucket.value.taxIndex; //Which tax index? (check if the bucket is upfront)
		const feeTaxBinaryArray = util.taxIndexToBinaryArray(taxIndex);
		const isInThisTaxLine = feeTaxBinaryArray[taxLineIndex] == '1';
		return isInThisTaxLine;
	}

	//TOGGLES AN BUCKET ITEM'S TAX LINE
	//EITHER SETTING SO THAT IT GETS TAXED
	//OR SETTING IT SO THAT IT DOES NOT GET TAXED
	//ON THE PROVIDED TAX LINE
	function toggleTaxLine(taxLineIndex: number) {
		const isSelected = !isInThisTaxLine(taxLineIndex);

		const originalBucketData = new FIMenuBuyersOrderBucket(bucket.value);

		//GET THE NEW TAX INDEX THIS ITEM WILL HAVE AFTER TOGGLE
		let newTaxIndex = bucket.value.taxIndex;
		let newUpfrontTaxIndex = bucket.value.upfrontTaxIndex;

		if (bo.value.isLease()) {
			//GET THE NEW UPFRONT TAX INDEX THIS ITEM WILL HAVE AFTER TOGGLE
			const upfrontFeeTaxBinaryArray = util.taxIndexToBinaryArray(bucket.value.upfrontTaxIndex);
			upfrontFeeTaxBinaryArray[taxLineIndex] = isSelected ? '1' : '0';
			newUpfrontTaxIndex = util.binaryArrayToTaxIndex(upfrontFeeTaxBinaryArray);
		} else {
			//GET THE NEW TAX INDEX THIS ITEM WILL HAVE AFTER TOGGLE
			const feeTaxBinaryArray = util.taxIndexToBinaryArray(bucket.value.taxIndex);
			feeTaxBinaryArray[taxLineIndex] = isSelected ? '1' : '0';
			newTaxIndex = util.binaryArrayToTaxIndex(feeTaxBinaryArray);
		}

		//CREATE NEW BUCKET WITH THE NEW TAX INDEX
		bucket.value = bo.value.addNewDefaultBucket(bucket.value.bucketType, newTaxIndex, newUpfrontTaxIndex, originalBucketData, null, bucket.value.originalTitleId);
		fee.value.titleId = bucket.value.titleId;
	}
	function deleteFee() {
		const result = confirm('Are you sure you want to delete this fee?');
		if (result) {
			const foundBucket = bo.value.buckets.find(bucket => bucket.items.some(item => item.name == originalName.value));
			if (!foundBucket) {
				util.toastr('error', 'Buyers Order', 'Could not remove fee');
				$modal.cancel();
			}

			bo.value.removeFee(feeIndex.value);
			bucket.value.removeItem(originalName.value);
			$modal.close(bo.value);
		}
	}
	function cancel() {
		$modal.cancel();
	}
	function save() {
		let item: FIMenuBuyersOrderBucketItem = bucket.value.items.find((i: FIMenuBuyersOrderBucketItem) => i.name == originalName.value);
		if (item) {
			item.name = fee.value.name;
			item.amount = fee.value.amount;
			item.cost = fee.value.cost;
		} else {
			item = new FIMenuBuyersOrderBucketItem({
				bucketItem: {
					amount: fee.value.amount,
					cost: fee.value.cost,
					profitFlag: fee.value.profitFlag,
					name: fee.value.name,
					sign: 1,
					// metadata: fee.value.metadata ?? null,
				},
				parentBucket: bucket.value,
			});

			bucket.value.items.push(item);
		}

		item.profit = fee.value.amount - fee.value.cost;

		// Update last edit info
		fee.value.editedById = user.EmployeeId;
		fee.value.editedByName = user.EmployeeName;
		fee.value.lastModifiedAt = new Date();

		if (isNewFee.value) {
			fee.value.createdById = user.EmployeeId;
			fee.value.createdByName = user.EmployeeName;
			fee.value.createdAt = new Date(fee.value.lastModifiedAt);
		}

		$modal.close(bo.value);
	}

	// We moved this logic outside of FIMenuBuyersOrderFee because of base Fee's validation method
	function hasUniqueName() {
		return !bo.value.fees.find(
			(boFee: FIMenuBuyersOrderFee) => fee.value.id !== boFee.id && boFee.name && fee.value.name && boFee.name.toLowerCase() === fee.value.name.toLowerCase(),
		);
	}

	const validations = computed(() => ({
		fee: {
			...(fee.value.validation() as any),
			name: {
				required,
				hasUniqueName,
			} as any,
		},
	}));

	const v$ = useVuelidate(validations, { fee });
</script>
<style>
	.bucket-list {
		margin-left: 15px;
	}

	.bucket-type-title {
		height: 25px;
	}

	.ModalAddEditFee .description {
		justify-content: flex-start;
	}

	.ModalAddEditFee .panel-header .InputDropdown .input-component .saturn-textbox select {
		padding: 5px 30px 5px 15px;
	}

	.ModalAddEditFee .modal-header .additional-info {
		font-size: 0.75rem;
		color: var(--third-color);
	}
</style>
